// Erasing a person (D83), for a privacy request: who they are, which organizations go with them
// and which stay, what blocks it, their email typed again and a reason; it runs after 7 days
// unless canceled. Scheduling needs a sign-in under an hour old. Every step is in both audit logs.
import { KV, PageHead, Section, Status } from '@apsio/ui'
import { useQuery } from '@tanstack/react-query'
import { Link, useParams } from '@tanstack/react-router'
import { scheduleUserErasure, useAdminMutation, userErasureQuery } from '../api/queries'
import { DangerZone } from '../components/kit'
import { useCrumbs } from '../components/Shell'
import { ErrorState, Loading } from '../components/states'
import { useI18n } from '../i18n'
import { OpenErasure } from './OrgErasure'

/** Erasing one person: who, what goes with them, what blocks it; the Danger zone last. */
export function UserErasurePage() {
  const { t } = useI18n()
  const { userId } = useParams({ strict: false }) as { userId: string }
  const preview = useQuery(userErasureQuery(userId))
  const refresh = [['user', userId], ['erasures']]
  const schedule = useAdminMutation(scheduleUserErasure, refresh)
  useCrumbs([{ label: t('erasure.user.title') }])
  const crumb = <Link to="/erasures">{t('erasure.title')}</Link>
  if (preview.isPending)
    return (
      <main id="main" className="pg">
        <Loading />
      </main>
    )
  if (preview.isError)
    return (
      <main id="main" className="pg">
        <PageHead crumb={crumb} title={t('erasure.user.title')} />
        <ErrorState error={preview.error} retry={() => preview.refetch()} />
      </main>
    )
  const p = preview.data
  return (
    <main id="main" className="pg">
      <PageHead
        crumb={crumb}
        title={t('erasure.user.title')}
        meta={<span translate="no">{p.user.email}</span>}
      />
      <Section title={t('erasure.user.what')}>
        <p className="er-line">{t('erasure.user.about')}</p>
        <KV
          items={[
            [t('erasure.user.deleted'), <OrgList key="d" orgs={p.deleted} />],
            [t('erasure.user.kept'), <OrgList key="k" orgs={p.kept} />],
          ]}
        />
        {p.blockers.length ? (
          // A state to resolve, not a failure: the warn word, the why, and where to fix it.
          <div className="er-block" role="note">
            <Status s="miss" glyph="!" label={t('erasure.user.blockedWord')} />
            <p>{t('erasure.user.blocked')}</p>
            <ul className="er-fix">
              {p.blockers.map((o) => (
                <li key={o.org_id}>
                  <Link to="/orgs/$orgId" params={{ orgId: o.org_id }}>
                    {t('erasure.user.openToChangeOwner', { name: o.name })}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </Section>
      {p.erasure ? (
        <OpenErasure erasure={p.erasure} refresh={refresh} />
      ) : p.user.erased ? (
        <p role="status" className="er-line">
          {t('erasure.user.erased')}
        </p>
      ) : p.user.staff ? (
        <p className="er-line">{t('erasure.user.staff')}</p>
      ) : p.blockers.length === 0 ? (
        <DangerZone
          title={t('erasure.user.action')}
          body={t('erasure.user.consequence')}
          action={t('erasure.user.action')}
          confirmTitle={t('erasure.user.confirmTitle', { email: p.user.email })}
          confirmBody={t('erasure.user.confirmBody')}
          confirmLabel={t('erasure.user.confirm')}
          typed={p.user.email}
          noEmails
          busy={schedule.isPending}
          error={schedule.error ? <ErrorState error={schedule.error} /> : null}
          onConfirm={(reason) => schedule.mutate({ userId, reason, confirm: p.user.email })}
        />
      ) : null}
    </main>
  )
}

/** Organizations as links with their members, inline; a dash when there is none. */
function OrgList({ orgs }: { orgs: { org_id: string; name: string; members: number }[] }) {
  const { t } = useI18n()
  if (orgs.length === 0) return <>{t('common.none')}</>
  return (
    <span className="er-orgs">
      {orgs.map((o) => (
        <span key={o.org_id}>
          <Link to="/orgs/$orgId" params={{ orgId: o.org_id }} translate="no">
            {o.name}
          </Link>{' '}
          <span className="dim">{t('erasure.members', { count: o.members })}</span>
        </span>
      ))}
    </span>
  )
}
